import { Check, Compass, Flag } from "lucide-react";
import { CourseChapter, CourseProgressFlow } from "types";

type JourneyStep = {
  key: string;
  title: string;
  label: string;
  flows: CourseProgressFlow[];
  chapter: boolean;
  chapterNumber?: number;
};

export function CourseJourney({
  chapters,
  flows,
  locked,
}: {
  chapters: CourseChapter[];
  flows: CourseProgressFlow[];
  locked: boolean;
}) {
  const orderedFlows = [...flows].sort((a, b) => a.order - b.order);
  const steps: JourneyStep[] = [];
  const seenChapters = new Set<number>();

  orderedFlows.forEach((flow, index) => {
    const chapter = chapters.find((item) => item.id === flow.chapter_id);
    if (chapter) {
      if (seenChapters.has(chapter.id)) return;
      seenChapters.add(chapter.id);
      steps.push({
        key: `chapter-${chapter.id}`,
        title: chapter.title,
        label: `Chapter ${seenChapters.size}`,
        flows: orderedFlows.filter((item) => item.chapter_id === chapter.id),
        chapter: true,
        chapterNumber: seenChapters.size,
      });
      return;
    }

    const previous = steps[steps.length - 1];
    if (previous && !previous.chapter) {
      previous.flows.push(flow);
      const firstIndex = orderedFlows.indexOf(previous.flows[0]) + 1;
      previous.title = `Modules ${firstIndex}–${index + 1}`;
      return;
    }
    steps.push({
      key: `lesson-${flow.id}`,
      title: flow.flow_details.title || flow.flow_details.name,
      label: index === 0 ? "Getting started" : `Module ${index + 1}`,
      flows: [flow],
      chapter: false,
    });
  });

  if (!seenChapters.size) return null;

  return (
    <section className="course-journey" aria-labelledby="course-journey-heading">
      <div className="course-journey-heading">
        <h2 id="course-journey-heading">
          <Compass size={19} className="course-journey-compass" aria-hidden="true" />
          Your journey
        </h2>
      </div>
      <ol className="course-journey-path" aria-label="Course journey">
        {steps.map((step, index) => {
          const completed = step.flows.filter((flow) => flow.completed_at).length;
          const done = completed === step.flows.length;
          const current = !locked && !done && step.flows.some(
            (flow) => flow.is_current && !flow.is_locked,
          );
          return (
            <li
              key={step.key}
              className={`course-journey-step${done ? " is-done" : ""}${current ? " is-current" : ""}`}
              aria-current={current ? "step" : undefined}
            >
              <span className="course-journey-marker" aria-hidden="true">
                {done ? <Check size={17} /> : index === 0 ? <Compass size={17} /> : index === steps.length - 1 ? <Flag size={16} /> : step.chapterNumber ?? index + 1}
              </span>
              <div className="course-journey-copy">
                <span className="course-journey-label">{step.label}</span>
                <strong>{step.title}</strong>
                <span className="course-journey-detail">
                  {step.flows.length} {step.flows.length === 1 ? "module" : "modules"}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
